// Activity Log state management hook
// Stores and manages log entries for completed tasks, check-ins, etc.

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'mabel-activity-log';
const MAX_ENTRIES = 500; // Keep last 500 entries to prevent unbounded growth

export const ENTRY_TYPES = {
  TASK_COMPLETED: 'task_completed',
  STATE_CHECKIN: 'state_checkin'
};

export default function useActivityLog() {
  const [entries, setEntries] = useState([]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Sort by timestamp descending (newest first)
          setEntries(parsed.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
        }
      } catch (e) {
        console.error('Failed to load activity log:', e);
      }
    }
  }, []);

  // Save to localStorage when entries change
  useEffect(() => {
    if (entries.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    }
  }, [entries]);

  // Add a new entry
  const addEntry = useCallback((entry) => {
    const newEntry = {
      id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
      timestamp: new Date().toISOString(),
      ...entry
    };

    setEntries(prev => {
      const updated = [newEntry, ...prev];
      // Trim to max entries
      if (updated.length > MAX_ENTRIES) {
        return updated.slice(0, MAX_ENTRIES);
      }
      return updated;
    });

    return newEntry;
  }, []);

  // Log a completed task
  const logTaskCompleted = useCallback((task) => {
    return addEntry({
      type: ENTRY_TYPES.TASK_COMPLETED,
      taskId: task.id,
      title: task.title,
      area: task.area,
      project: task.project,
      priority: task.priority
    });
  }, [addEntry]);

  // Log a state check-in
  const logStateCheckin = useCallback((state) => {
    return addEntry({
      type: ENTRY_TYPES.STATE_CHECKIN,
      focus: state.focus,
      feeling: state.feeling,
      location: state.location
    });
  }, [addEntry]);

  // Get entries filtered by type
  const getEntriesByType = useCallback((type) => {
    return entries.filter(e => e.type === type);
  }, [entries]);

  // Get entries from today
  const getTodayEntries = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return entries.filter(e => new Date(e.timestamp) >= today);
  }, [entries]);

  // Get stats
  const getStats = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayEntries = entries.filter(e => new Date(e.timestamp) >= today);

    return {
      tasksToday: todayEntries.filter(e => e.type === ENTRY_TYPES.TASK_COMPLETED).length,
      checkInsToday: todayEntries.filter(e => e.type === ENTRY_TYPES.STATE_CHECKIN).length,
      totalTasks: entries.filter(e => e.type === ENTRY_TYPES.TASK_COMPLETED).length,
      totalCheckIns: entries.filter(e => e.type === ENTRY_TYPES.STATE_CHECKIN).length
    };
  }, [entries]);

  // Clear all entries (for testing/reset)
  const clearLog = useCallback(() => {
    setEntries([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    entries,
    addEntry,
    logTaskCompleted,
    logStateCheckin,
    getEntriesByType,
    getTodayEntries,
    getStats,
    clearLog
  };
}
